import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { useIsMobile } from "../lib/useIsMobile";
import { useViewState } from "../lib/useViewState";
import {
  getSessionStorage,
  intMin,
  oneOf,
  str,
  writeNavScroll,
} from "../lib/viewState";
import {
  cachedMeetings,
  clearMeetingCaches,
  meetingBucket,
  meetingCacheEpoch,
  meetingDate,
  meetingDay,
  meetingDuration,
  meetingExcerpt,
  meetingShortDate,
  meetingShortDuration,
  meetingSyncLabel,
  meetingTime,
  rememberMeetings,
} from "../lib/meetings";
import {
  fathomRecordingUrl,
  transcriptSourceText,
  type MeetingDetail,
  type MeetingListItem,
  type MeetingProjectRef,
  type MeetingsResponse,
  type MeetingSyncStatus,
} from "../../shared/meetings";
import { PHASE_LABELS, type ProjectWithClient } from "../../shared/types";
import { formatProjectNumber } from "../lib/calendar";
import { PHASE_DOTS } from "../lib/phaseDots";
import { melbourneToday } from "../../shared/dates";
import {
  Dropdown,
  Modal,
  PageHeader,
  SearchBox,
  SkeletonRows,
  SortButton,
  Tabs,
} from "../components/primitives";
import { SwipeRow } from "../components/SwipeRow";
import MeetingMarkdown from "../components/MeetingMarkdown";
import { SyncIcon, TrashIcon } from "../components/icons";
const viewCodec = {
  query: str("q"),
  project: oneOf("project", ["all", "linked", "none"] as const, "all"),
  sort: oneOf("sort", ["newest", "oldest"] as const, "newest"),
  page: intMin("page", 1, 1),
};
const PROJECT_TABS = [
  { value: "all", label: "All" },
  { value: "linked", label: "Linked" },
  { value: "none", label: "No project" },
] as const;
const SORT_OPTIONS = [
  {
    key: "recorded",
    label: "Recorded",
    natural: "desc",
    naturalLabel: "newest first",
  },
] as const;
const busyStatus = (s: MeetingSyncStatus | null) =>
  s?.state === "importing" || s?.state === "syncing";
function useMeetingStatus() {
  const [status, setStatus] = useState<MeetingSyncStatus | null>(null);
  const [statusFailed, setFailed] = useState(false);
  const refresh = useCallback(async () => {
    try {
      const next = await api<MeetingSyncStatus>("/api/admin/meetings/status");
      setStatus(next);
      setFailed(false);
      return next;
    } catch {
      setFailed(true);
      return null;
    }
  }, []);
  useEffect(() => {
    let live = true;
    const load = async () => {
      if (live) await refresh();
    };
    void load();
    const timer = setInterval(load, 10_000);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [refresh]);
  return { status, statusFailed, refresh };
}
/** Header-right live slot: label plus dot tone. */
function statusHeader(status: MeetingSyncStatus | null, failed: boolean) {
  return {
    liveLabel: failed ? "Fathom status unavailable" : meetingSyncLabel(status),
    liveError: failed || status?.state === "failed",
    liveWarn: status?.state === "disconnected",
  };
}
/** One quiet amber line under the toolbar, only when something is wrong or
    still arriving. Saved meetings stay readable in every state. */
function SyncStrip({
  status,
  failed,
  onRetry,
}: {
  status: MeetingSyncStatus | null;
  failed: boolean;
  onRetry: () => void;
}) {
  const retry = (
    <button type="button" className="mt-strip-act" onClick={onRetry}>
      Retry →
    </button>
  );
  if (failed)
    return (
      <div className="mt-strip" role="alert">
        Fathom status couldn’t be reached — saved meetings are still available.
        {retry}
      </div>
    );
  if (status?.state === "failed")
    return (
      <div className="mt-strip" role="alert">
        Fathom couldn’t sync — saved meetings are still available.{" "}
        {status.error_code === "rate_limited"
          ? "Fathom has asked us to wait; the import will resume automatically."
          : "The import will retry automatically."}
        {retry}
      </div>
    );
  if (status?.state === "importing")
    return (
      <div className="mt-strip" role="status">
        Importing your older recordings — you can read meetings as they arrive.
        {status.processed > 0 && ` ${status.processed} processed.`}
      </div>
    );
  if (status?.state === "syncing")
    return (
      <div className="mt-strip" role="status">
        Syncing your recordings — you can keep reading saved notes.
      </div>
    );
  if (status?.state === "disconnected")
    return (
      <div className="mt-strip" role="status">
        Fathom isn’t connected — saved meetings stay readable, new calls won’t
        arrive.
      </div>
    );
  return null;
}
type ProjectOption = Pick<
  ProjectWithClient,
  "id" | "name" | "project_number" | "phase" | "client_name"
>;
/** Live, unvoided projects — the picker's options and the tags' number and
    phase. Tags fall back to the meeting's own {id, name} until loaded. */
function useProjectOptions() {
  const [projects, setProjects] = useState<ProjectOption[] | null>(null);
  useEffect(() => {
    let live = true;
    api<{ projects: ProjectWithClient[] }>("/api/admin/projects")
      .then((d) => {
        if (live) setProjects(d.projects.filter((p) => p.voided_at === null));
      })
      .catch(() => {
        if (live) setProjects([]);
      });
    return () => {
      live = false;
    };
  }, []);
  return projects;
}
const closedPhase = (p: ProjectOption) =>
  p.phase === "delivered" || p.phase === "closed";
/** "#0201 Patient Welcome Film" — the list row's project link. */
function RowProjectTag({
  projects,
  byId,
}: {
  projects: MeetingProjectRef[];
  byId: Map<string, ProjectOption>;
}) {
  if (!projects.length) return <span className="mt-unlinked">No project</span>;
  const [first] = projects;
  const number = formatProjectNumber(byId.get(first.id)?.project_number);
  return (
    <Link
      className="project-tag mt-tag"
      to={"/projects/" + first.id}
      onClick={(e) => e.stopPropagation()}
      title={projects.map((p) => p.name).join(", ")}
    >
      {number && <span className="n">{number}</span>}
      <span className="mt-tag-name">{first.name}</span>
      {projects.length > 1 && (
        <span
          className="n mt-tag-more"
          title={projects.slice(1).map((p) => p.name).join(", ")}
        >
          +{projects.length - 1}
        </span>
      )}
    </Link>
  );
}
function RailProjectTag({
  project,
  info,
  onRemove,
  disabled,
}: {
  project: MeetingProjectRef;
  info?: ProjectOption;
  onRemove: () => void;
  disabled?: boolean;
}) {
  const number = formatProjectNumber(info?.project_number);
  return (
    <span className="project-tag mt-tag">
      <Link
        to={"/projects/" + project.id}
        title={info ? `${project.name} · ${PHASE_LABELS[info.phase]}` : project.name}
      >
        {number && <span className="n">{number}</span>}
        <span className="mt-tag-name">{project.name}</span>
      </Link>
      <button
        type="button"
        className="mt-tag-x"
        aria-label={`Unlink ${project.name}`}
        disabled={disabled}
        onClick={onRemove}
      >
        ×
      </button>
    </span>
  );
}
function MeetingProjects({
  meeting,
  projects,
  onSaved,
}: {
  meeting: MeetingDetail;
  projects: ProjectOption[] | null;
  onSaved: (projects: MeetingProjectRef[]) => void;
}) {
  const [open, setOpen] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  // Shown optimistically while the PUT is in flight; reverts on failure.
  const [pending, setPending] = useState<MeetingProjectRef[] | null>(null);
  const linked = pending ?? meeting.projects;
  const byId = new Map((projects ?? []).map((p) => [p.id, p]));
  const save = async (next: MeetingProjectRef[]) => {
    setBusy(true);
    setError("");
    setPending(next);
    try {
      const res = await api<{ projects: MeetingProjectRef[] }>(
        `/api/admin/meetings/${meeting.id}/projects`,
        {
          method: "PUT",
          json: { project_ids: next.map((p) => p.id) },
        },
      );
      clearMeetingCaches();
      onSaved(res.projects);
    } catch {
      setError("Project links couldn’t be saved. Please retry.");
    } finally {
      setPending(null);
      setBusy(false);
    }
  };
  const available = (projects ?? [])
    .filter((p) => !linked.some((l) => l.id === p.id))
    .sort((a, b) => Number(closedPhase(a)) - Number(closedPhase(b)));
  return (
    <div className="field">
      <div className="field-label">Projects</div>
      {linked.length > 0 && (
        <div className="mt-rail-tags">
          {linked.map((p) => (
            <RailProjectTag
              key={p.id}
              project={p}
              info={byId.get(p.id)}
              disabled={busy}
              onRemove={() => void save(linked.filter((l) => l.id !== p.id))}
            />
          ))}
        </div>
      )}
      <span className="dropdown-anchor">
        <button
          type="button"
          className="mt-link-add"
          aria-expanded={open}
          disabled={busy || projects === null}
          onClick={() => setOpen((o) => !o)}
        >
          + Link project
        </button>
        {open && (
          <Dropdown
            searchable
            label="Link project"
            items={available.map((p) => {
              const number = formatProjectNumber(p.project_number);
              return {
                value: p.id,
                label: `${number ? `${number} · ` : ""}${p.name}`,
                dotColor: PHASE_DOTS[p.phase],
                sub: `${p.client_name} · ${PHASE_LABELS[p.phase]}`,
                group: closedPhase(p) ? "Delivered & closed" : "Active",
              };
            })}
            onSelect={(id) => {
              const picked = byId.get(id);
              if (picked)
                void save([...linked, { id: picked.id, name: picked.name }]);
            }}
            onClose={() => setOpen(false)}
          />
        )}
      </span>
      {error && (
        <p className="mt-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
function Removal({
  meeting,
  onClose,
  onRemoved,
}: {
  meeting: MeetingListItem;
  onClose: () => void;
  onRemoved: () => void;
}) {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const mobile = useIsMobile(),
    bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (mobile) return; // Native Sheet owns mobile focus/keyboard behavior.
    const dialog = bodyRef.current?.closest<HTMLElement>('[role="dialog"]');
    if (!dialog) return;
    const opener = document.activeElement as HTMLElement | null;
    dialog.tabIndex = -1;
    dialog.setAttribute("aria-label", "Remove from OS?");
    dialog.focus({ preventScroll: true });
    const trap = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const buttons = [
        ...dialog.querySelectorAll<HTMLButtonElement>("button:not([disabled])"),
      ];
      const first = buttons[0],
        last = buttons[buttons.length - 1];
      if (!first) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      if (
        !buttons.includes(document.activeElement as HTMLButtonElement) ||
        (e.shiftKey && document.activeElement === first) ||
        (!e.shiftKey && document.activeElement === last)
      ) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      }
    };
    dialog.addEventListener("keydown", trap);
    return () => {
      dialog.removeEventListener("keydown", trap);
      if (opener && document.contains(opener))
        opener.focus({ preventScroll: true });
    };
  }, [mobile]);
  const remove = async () => {
    setBusy(true);
    setError("");
    try {
      await api("/api/admin/meetings/" + meeting.id, { method: "DELETE" });
      clearMeetingCaches();
      onRemoved();
    } catch {
      setError("Meeting couldn’t be removed. Please retry.");
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Remove from OS?"
      context={meeting.title}
      width={470}
      mobile="half"
      onClose={() => {
        if (!busy) onClose();
      }}
      foot={
        <>
          <button className="btn" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn danger mt-remove-confirm"
            data-confirm
            disabled={busy}
            onClick={remove}
          >
            {busy ? "Removing…" : "Remove meeting"}
          </button>
        </>
      }
    >
      <div className="remove-copy" ref={bodyRef}>
        <p>
          This meeting will leave your library and move to Trash. Future Fathom
          syncs will skip it.
        </p>
        <p>
          You can restore it from Settings → Data → Trash for 30 days. After
          that, its saved summary and transcript are deleted.
        </p>
        <p className="mt-muted">The recording stays in Fathom.</p>
        {error && (
          <p className="mt-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
const participantNames = (m: MeetingListItem) =>
  m.participants
    .map((p) => p.name || p.email)
    .filter(Boolean)
    .join(", ");
/** Rows in order, split into consecutive date groups (works for both sort
    directions since rows arrive already sorted by recorded time). */
function groupRows(rows: MeetingListItem[]) {
  const groups: { label: string; rows: MeetingListItem[] }[] = [];
  const today = melbourneToday();
  for (const m of rows) {
    const label = meetingBucket(m.recorded_at, today);
    const last = groups[groups.length - 1];
    if (last?.label === label) last.rows.push(m);
    else groups.push({ label, rows: [m] });
  }
  return groups;
}
export default function Meetings() {
  const { initial, publish } = useViewState(viewCodec, (state) =>
    setView(state),
  );
  const [view, setView] = useState(initial);
  const isMobile = useIsMobile();
  // The phone list has no tabs or sort (approved mockup), so it always
  // shows All, newest first.
  const projectFilter = isMobile ? "all" : view.project;
  const sort = isMobile ? "newest" : view.sort;
  const page =
    projectFilter !== view.project || sort !== view.sort ? 1 : view.page;
  const params = new URLSearchParams({
    q: view.query.trim(),
    sort,
    page: String(page),
  });
  if (projectFilter !== "all") params.set("project", projectFilter);
  const key = params.toString();
  const [data, setData] = useState<MeetingsResponse | null>(() =>
    cachedMeetings(key),
  );
  const [loading, setLoading] = useState(() => !cachedMeetings(key)),
    [error, setError] = useState("");
  const [confirm, setConfirm] = useState<MeetingListItem | null>(null);
  const location = useLocation(),
    navigate = useNavigate();
  const [notice, setNotice] = useState(() =>
    (location.state as { meetingRemoved?: boolean } | null)?.meetingRemoved
      ? "Meeting moved to Trash. Future syncs will skip it."
      : "",
  );
  const { status, statusFailed, refresh } = useMeetingStatus();
  const seq = useRef(0),
    [reload, setReload] = useState(0);
  useEffect(() => publish(view), [view, publish]);
  useEffect(() => {
    const request = ++seq.current,
      epoch = meetingCacheEpoch();
    const cached = cachedMeetings(key);
    setData(cached);
    setLoading(!cached);
    setError("");
    const timer = setTimeout(
      () => {
        api<MeetingsResponse>("/api/admin/meetings?" + key)
          .then((next) => {
            if (request !== seq.current || epoch !== meetingCacheEpoch())
              return;
            rememberMeetings(key, next, epoch);
            setData(next);
            setLoading(false);
          })
          .catch(() => {
            if (request === seq.current) {
              setError("Meetings couldn’t be reached. Please retry.");
              setLoading(false);
            }
          });
      },
      view.query ? 250 : 0,
    );
    return () => {
      clearTimeout(timer);
      seq.current++;
    };
  }, [key, reload]);
  const previousLibraryRevision = useRef<number | undefined>(undefined);
  useEffect(() => {
    const revision = status?.library_revision;
    if (
      previousLibraryRevision.current !== undefined &&
      previousLibraryRevision.current !== revision
    )
      clearMeetingCaches();
    previousLibraryRevision.current = revision;
    setReload((v) => v + 1);
  }, [
    status?.library_revision,
    status?.last_provider_success_at,
    status?.processed,
    status?.last_sync_at,
  ]);
  useEffect(() => {
    const mutated = () => {
      clearMeetingCaches();
      setReload((v) => v + 1);
    };
    window.addEventListener("ss:mutated", mutated);
    return () => window.removeEventListener("ss:mutated", mutated);
  }, []);
  const sync = async () => {
    setError("");
    try {
      await api<MeetingSyncStatus>("/api/admin/meetings/sync", {
        method: "POST",
      });
      await refresh();
    } catch {
      setError("Sync couldn’t start. Please retry.");
    }
  };
  const returnTo = location.pathname + location.search;
  const rememberScroll = () =>
    writeNavScroll(
      getSessionStorage(),
      "/meetings",
      document.querySelector(".page-body")?.scrollTop ?? 0,
    );
  const openMeeting = (m: MeetingListItem) => {
    rememberScroll();
    navigate("/meetings/" + m.id, { state: { returnTo } });
  };
  const rows = data?.meetings ?? [];
  const projects = useProjectOptions();
  const projectsById = new Map((projects ?? []).map((p) => [p.id, p]));
  const totalPages = Math.max(
    1,
    Math.ceil((data?.total ?? 0) / (data?.page_size ?? 20)),
  );
  const setPage = (page: number) =>
    setView((v) => ({ ...v, project: projectFilter, sort, page }));
  const emptyText = view.query
    ? `No meetings match “${view.query.trim()}”.`
    : projectFilter === "linked"
      ? "No meetings are linked to a project yet."
      : projectFilter === "none"
        ? "Every meeting is linked to a project."
        : status?.state === "disconnected"
          ? "No saved meetings yet. They’ll appear here once Fathom is connected."
          : "No recordings yet. New meetings appear once Fathom finishes preparing their notes.";
  const row = (m: MeetingListItem) => {
    const excerpt =
      meetingExcerpt(m.excerpt) ||
      (m.summary_ready ? "Open the original notes." : "Summary being prepared");
    const people = participantNames(m);
    if (isMobile)
      return (
        <SwipeRow
          key={m.id}
          leftActions={[
            {
              label: "Remove",
              icon: <TrashIcon />,
              tone: "danger",
              onSelect: () => setConfirm(m),
            },
          ]}
        >
          <div className="mtr-m" onClick={() => openMeeting(m)}>
            <Link
              className="t1"
              to={"/meetings/" + m.id}
              state={{ returnTo }}
              onClick={(e) => {
                e.stopPropagation();
                rememberScroll();
              }}
            >
              {m.title}
            </Link>
            <div className="t2">
              {[
                meetingDay(m.recorded_at),
                meetingTime(m.recorded_at),
                meetingShortDuration(m.duration_seconds),
                people,
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
            <div className="t3">{excerpt}</div>
            {!m.transcript_ready && (
              <div className="mt-flag">Transcript being prepared</div>
            )}
            {m.projects.length > 0 && (
              <RowProjectTag projects={m.projects} byId={projectsById} />
            )}
          </div>
        </SwipeRow>
      );
    return (
      <div className="row mtr" key={m.id} onClick={() => openMeeting(m)}>
        <span className="mtr-date">
          {meetingDay(m.recorded_at)}
          <small>{meetingTime(m.recorded_at)}</small>
        </span>
        <div className="mtr-main">
          <span className="mtr-title">
            <Link
              className="row-title"
              to={"/meetings/" + m.id}
              state={{ returnTo }}
              onClick={(e) => {
                e.stopPropagation();
                rememberScroll();
              }}
            >
              {m.title}
            </Link>
            {!m.transcript_ready && (
              <span className="mt-flag">Transcript being prepared</span>
            )}
          </span>
          <span className="mtr-excerpt">{excerpt}</span>
        </div>
        <div className="mtr-side">
          {people && (
            <span className="mtr-people" title={people}>
              {people}
            </span>
          )}
          <span className="mtr-dur">
            {meetingShortDuration(m.duration_seconds)}
          </span>
          <RowProjectTag projects={m.projects} byId={projectsById} />
        </div>
        <button
          type="button"
          className="row-x"
          title="Remove from OS"
          aria-label={`Remove ${m.title} from OS`}
          onClick={(e) => {
            e.stopPropagation();
            setConfirm(m);
          }}
        >
          ×
        </button>
      </div>
    );
  };
  return (
    <>
      <PageHeader
        title="Meetings"
        count={data ? `${data.total} recordings` : undefined}
        {...statusHeader(status, statusFailed)}
        mobileActions={
          status && status.state !== "disconnected"
            ? [
                {
                  key: "sync",
                  label: "Sync now",
                  icon: <SyncIcon />,
                  onPress: () => {
                    if (!busyStatus(status)) void sync();
                  },
                },
              ]
            : undefined
        }
      />
      {isMobile ? (
        <div className="toolbar meetings-toolbar">
          <SearchBox
            placeholder="Search meetings…"
            value={view.query}
            onChange={(query) => setView((v) => ({ ...v, query, page: 1 }))}
          />
        </div>
      ) : (
        <div className="toolbar meetings-toolbar">
          <Tabs
            options={PROJECT_TABS}
            value={view.project}
            onChange={(project) => setView((v) => ({ ...v, project, page: 1 }))}
          />
          <SortButton
            options={SORT_OPTIONS}
            value={{
              key: "recorded",
              dir: view.sort === "newest" ? "desc" : "asc",
            }}
            onChange={(next) =>
              setView((v) => ({
                ...v,
                sort: next.dir === "desc" ? "newest" : "oldest",
                page: 1,
              }))
            }
          />
          <SearchBox
            placeholder="Search titles, people, notes, transcripts…"
            value={view.query}
            onChange={(query) => setView((v) => ({ ...v, query, page: 1 }))}
          />
          <button
            className="btn"
            onClick={sync}
            disabled={
              !status || status.state === "disconnected" || busyStatus(status)
            }
          >
            ↻ Sync now
          </button>
        </div>
      )}
      <div className="page-body">
        <div className="content wide mt-list">
          {notice && (
            <div className="mt-strip ok" role="status">
              {notice}
              <button
                type="button"
                className="mt-strip-act"
                aria-label="Dismiss"
                onClick={() => setNotice("")}
              >
                ×
              </button>
            </div>
          )}
          <SyncStrip
            status={status}
            failed={statusFailed}
            onRetry={() => {
              void refresh();
              if (status?.state !== "disconnected") void sync();
            }}
          />
          {error && (
            <div className="mt-strip" role="alert">
              {error}
              <button
                type="button"
                className="mt-strip-act"
                onClick={() => setReload((v) => v + 1)}
              >
                Retry →
              </button>
            </div>
          )}
          {loading ? (
            <SkeletonRows rows={4} />
          ) : rows.length ? (
            <>
              {groupRows(rows).map((g) => (
                <div className="bucket" key={g.label}>
                  <div className="group-head">
                    <span className="group-title">{g.label}</span>
                    <span className="group-count">{g.rows.length}</span>
                  </div>
                  {g.rows.map(row)}
                </div>
              ))}
              <div className="mt-foot">
                {isMobile
                  ? "Swipe left to remove · tap to read"
                  : "Times in Melbourne · click a row to read · × removes from OS (Fathom keeps it)"}
              </div>
            </>
          ) : (
            !error && (
              <div className="empty-note mt-empty">
                {emptyText}
                {view.query && (
                  <button
                    type="button"
                    className="group-link"
                    onClick={() =>
                      setView((v) => ({ ...v, query: "", page: 1 }))
                    }
                  >
                    Clear search
                  </button>
                )}
              </div>
            )
          )}
          {!loading && (totalPages > 1 || page > 1) && (
            <nav className="mt-pagination" aria-label="Meeting pages">
              <button
                className="btn"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                ← Previous
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                className="btn"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next →
              </button>
            </nav>
          )}
        </div>
      </div>
      {confirm && (
        <Removal
          meeting={confirm}
          onClose={() => setConfirm(null)}
          onRemoved={() => {
            setData((d) =>
              d
                ? {
                    ...d,
                    total: d.total - 1,
                    meetings: d.meetings.filter((m) => m.id !== confirm.id),
                  }
                : d,
            );
            setNotice("Meeting moved to Trash. Future syncs will skip it.");
            setConfirm(null);
            setReload((v) => v + 1);
          }}
        />
      )}
    </>
  );
}
/** Wraps each case-insensitive occurrence of `find` in a gold <mark>. */
function Highlight({ text, find }: { text: string; find: string }) {
  const needle = find.trim();
  if (!needle) return <>{text}</>;
  const parts = text.split(
    new RegExp(`(${needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"),
  );
  return (
    <>
      {parts.map((part, i) =>
        i % 2 ? <mark key={i}>{part}</mark> : <Fragment key={i}>{part}</Fragment>,
      )}
    </>
  );
}
export function MeetingDetailPage() {
  const { id } = useParams(),
    location = useLocation(),
    navigate = useNavigate();
  const isMobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "transcript" ? "transcript" : "summary";
  const [meeting, setMeeting] = useState<MeetingDetail | null>(null),
    [error, setError] = useState(""),
    [unavailable, setUnavailable] = useState(false);
  const [confirm, setConfirm] = useState(false),
    [find, setFind] = useState(""),
    [copyNotice, setCopyNotice] = useState("");
  const { status, statusFailed, refresh } = useMeetingStatus();
  const projects = useProjectOptions();
  const [reload, setReload] = useState(0);
  const stateReturn = (location.state as { returnTo?: string } | null)
    ?.returnTo;
  const returnTo =
    stateReturn && /^\/meetings(?:\?|$)/.test(stateReturn)
      ? stateReturn
      : "/meetings";
  useEffect(() => {
    setMeeting(null);
    setError("");
    setUnavailable(false);
    setFind("");
    setCopyNotice("");
  }, [id]);
  useEffect(() => {
    let live = true;
    api<MeetingDetail>("/api/admin/meetings/" + id)
      .then((m) => {
        if (live) {
          setMeeting(m);
          setError("");
          setUnavailable(false);
        }
      })
      .catch((e) => {
        if (live) {
          if (e instanceof ApiError && e.status === 404) setMeeting(null);
          setUnavailable(e instanceof ApiError && e.status === 404);
          setError(
            e instanceof ApiError && e.status === 404
              ? "Meeting unavailable. It may have been removed."
              : "Meeting couldn’t be reached. Please retry.",
          );
        }
      });
    return () => {
      live = false;
    };
  }, [id, reload]);
  // Signed arrivals, reconciliation and Trash operations all invalidate content.
  // Preserve the active tab/find/copy state while refreshing an open detail.
  const previousRevision = useRef<number | undefined>(undefined);
  useEffect(() => {
    const revision = status?.library_revision;
    if (revision === undefined) return;
    if (previousRevision.current !== revision) setReload((v) => v + 1);
    previousRevision.current = revision;
  }, [status?.library_revision]);
  const copy = async () => {
    if (!meeting) return;
    const text =
      tab === "summary"
        ? meeting.summary_markdown
        : meeting.transcript && transcriptSourceText(meeting.transcript);
    if (text === null) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopyNotice(`${tab === "summary" ? "Summary" : "Transcript"} copied.`);
    } catch {
      setCopyNotice("Copy failed. Please try again.");
    }
  };
  const recordingUrl = fathomRecordingUrl(meeting?.recording_url);
  const turns =
    meeting?.transcript?.filter((s) =>
      `${s.timestamp} ${s.speaker} ${s.text}`
        .toLowerCase()
        .includes(find.toLowerCase()),
    ) ?? [];
  const title = meeting?.title ?? (error ? "Unavailable" : "Loading…");
  const fathomAction = recordingUrl ? (
    <a
      className="btn primary"
      href={recordingUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-fathom
    >
      Open in Fathom ↗
    </a>
  ) : (
    <span className="mt-disabled-tip" title="Recording link unavailable">
      <button
        className="btn primary"
        disabled
        aria-describedby="mt-recording-unavailable"
      >
        Open in Fathom ↗
      </button>
      <span id="mt-recording-unavailable" className="sr-only">
        Recording link unavailable
      </span>
    </span>
  );
  const actions = meeting && (
    <>
      {isMobile && fathomAction}
      <button className="btn danger" onClick={() => setConfirm(true)}>
        Remove
      </button>
      <button
        className="btn"
        disabled={
          tab === "summary" ? !meeting.summary_ready : !meeting.transcript_ready
        }
        onClick={copy}
      >
        Copy {tab}
      </button>
      {!isMobile && fathomAction}
    </>
  );
  const people = meeting ? participantNames(meeting) : "";
  return (
    <>
      <header className="detail-header mt-detail-header">
        <Link className="crumb" to={returnTo}>
          {isMobile ? "‹ Meetings" : "Meetings"}
        </Link>
        {!isMobile && (
          <>
            <span className="crumb-sep">/</span>
            <span className="crumb-current">{title}</span>
            <div className="detail-actions">{actions}</div>
          </>
        )}
      </header>
      <div className="page-body">
        {error ? (
          <div className="detail-main mt-detail-error">
            <div className="empty-note" role="alert">
              {error}
              {!unavailable && (
                <button
                  type="button"
                  className="group-link"
                  onClick={() => setReload((v) => v + 1)}
                >
                  Retry
                </button>
              )}
            </div>
          </div>
        ) : !meeting ? (
          <div className="detail-main">
            <SkeletonRows rows={4} />
          </div>
        ) : (
          <div className="detail-grid mt-detail">
            <div className="detail-main">
              <h1 className="detail-name">{meeting.title}</h1>
              <div className="detail-sub">
                {[
                  meetingDay(meeting.recorded_at),
                  meetingTime(meeting.recorded_at),
                  meeting.duration_seconds !== null &&
                    meetingDuration(meeting.duration_seconds),
                  people && `with ${people}`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
              {isMobile && <div className="mt-actions-m">{actions}</div>}
              <SyncStrip
                status={status}
                failed={statusFailed}
                onRetry={() => void refresh()}
              />
              <div className="group">
                <div className="group-head mt-tabs-head">
                  <div className="tabs" aria-label="Meeting content">
                    {(["summary", "transcript"] as const).map((t) => (
                      <button
                        key={t}
                        type="button"
                        className={`tab${tab === t ? " active" : ""}`}
                        aria-pressed={tab === t}
                        onClick={() => {
                          setParams(t === "transcript" ? { tab: t } : {});
                          setCopyNotice("");
                        }}
                      >
                        {t === "summary" ? "Summary" : "Transcript"}
                      </button>
                    ))}
                  </div>
                  {copyNotice ? (
                    <span
                      className="group-count mt-copy-notice"
                      role={
                        copyNotice.startsWith("Copy failed")
                          ? "alert"
                          : "status"
                      }
                    >
                      {copyNotice}
                    </span>
                  ) : (
                    <span className="group-count">
                      from Fathom · read-only
                    </span>
                  )}
                </div>
                {tab === "summary" ? (
                  meeting.summary_ready ? (
                    <div className="mt-summary">
                      <MeetingMarkdown text={meeting.summary_markdown!} />
                    </div>
                  ) : (
                    <div className="empty-note mt-empty">
                      Summary being prepared.{" "}
                      {meeting.transcript_ready
                        ? "The transcript is ready. "
                        : ""}
                      The summary will appear after a successful sync.
                    </div>
                  )
                ) : !meeting.transcript_ready ? (
                  <div className="empty-note mt-empty">
                    Transcript being prepared.{" "}
                    {meeting.summary_ready ? "The summary is ready. " : ""}
                    The transcript will appear after a successful sync.
                  </div>
                ) : (
                  <>
                    <div className="mt-find">
                      <SearchBox
                        placeholder="Find in transcript…"
                        value={find}
                        onChange={setFind}
                      />
                    </div>
                    {find.trim() && (
                      <div className="mt-foot mt-find-count">
                        {turns.length} match{turns.length === 1 ? "" : "es"} ·
                        find filters the view, copy takes the full transcript
                      </div>
                    )}
                    {turns.length ? (
                      turns.map((s, i) => (
                        <div className="mt-seg" key={i}>
                          <span className="t">{s.timestamp}</span>
                          <span className="s" title={s.speaker}>
                            <Highlight text={s.speaker} find={find} />
                          </span>
                          <span className="x">
                            <Highlight text={s.text} find={find} />
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="empty-note mt-empty">
                        {find
                          ? "No matching transcript lines."
                          : "This recording has an empty transcript."}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="detail-rail">
              <MeetingProjects
                key={meeting.id}
                meeting={meeting}
                projects={projects}
                onSaved={(next) =>
                  // A save that resolves after navigating away must not
                  // overwrite the next meeting's projects.
                  setMeeting((m) =>
                    m && m.id === meeting.id ? { ...m, projects: next } : m,
                  )
                }
              />
              <div className="field">
                <div className="field-label">
                  Participants · {meeting.participants.length}
                </div>
                {meeting.participants.map((p, i) => (
                  <div className="mt-person" key={i}>
                    <span>{p.name || p.email || "Unnamed participant"}</span>
                    {p.email && p.name && <span className="e">{p.email}</span>}
                  </div>
                ))}
              </div>
              <div className="field">
                <div className="field-label">Recorded</div>
                <div className="field-value mono">
                  {meetingDate(meeting.recorded_at, true)} ·{" "}
                  {meetingTime(meeting.recorded_at)}
                </div>
              </div>
              <div className="field">
                <div className="field-label">Duration</div>
                <div className="field-value mono">
                  {meetingDuration(meeting.duration_seconds)}
                </div>
              </div>
              <div className="field">
                <div className="field-label">Source</div>
                <div className="field-value">Fathom</div>
                <div className="mt-source-note">
                  {meeting.source_fetched_at > 0
                    ? `Fetched ${meetingShortDate(meeting.source_fetched_at)}, ${meetingTime(meeting.source_fetched_at)}`
                    : "Received via Fathom webhook"}{" "}
                  · edit notes in Fathom
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {meeting && confirm && (
        <Removal
          meeting={meeting}
          onClose={() => setConfirm(false)}
          onRemoved={() =>
            navigate(returnTo, { state: { meetingRemoved: true } })
          }
        />
      )}
    </>
  );
}
